import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const ConfirmContext = createContext(null);

/**
 * Wrap your app tree with <ConfirmProvider>. Any component can then call:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, description, confirmText, destructive });
 *   if (!ok) return;
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, opts: {} });
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({ open: true, opts });
    });
  }, []);

  const close = (result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver.current) { resolver.current(result); resolver.current = null; }
  };

  const {
    title = "Are you sure?",
    description = "This action cannot be undone.",
    confirmText = "Delete",
    cancelText = "Cancel",
    destructive = true,
  } = state.opts;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent className="rounded-none bg-card border-border" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-black tracking-tight">{title}</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">{description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={() => close(false)} className="rounded-none" data-testid="confirm-cancel">{cancelText}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => close(true)}
              data-testid="confirm-accept"
              className={`rounded-none font-bold ${destructive ? "bg-destructive hover:bg-destructive/90 text-destructive-foreground" : "bg-[#007AFF] hover:bg-[#3395FF] text-white"}`}
            >{confirmText}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return ctx;
}
